import { Action } from 'redux'
import { combineEpics, Epic, ofType } from 'redux-observable'
import { from, map, mergeMap } from 'rxjs'
import {
  cancelOrders,
  CreateOrderAction,
  createOrders,
  CreateOrdersAction
} from '../order/actions'
import { getOrderBySecurityAndType, getUserOrders } from '../order/selectors'
import { RemoveStagedOrderAction } from '../stagedOrders/actions'
import { getStagedOrdersByTypeToArray } from '../stagedOrders/selectors'
import { getDetailsForCurrentWatchlist } from '../watchList/selectors'
import {
  applyTempCheckedOrders,
  cancelCheckedOrders,
  CancelCheckedOrdersAction,
  CheckOrUncheckAllAction,
  resetCheckedOrders,
  uncheckOrder,
  uncheckOrders,
  updateCheckedOrders
} from './actions'
import {
  checkedOrdersStatus,
  getCheckedOrders,
  getCheckedStagedOrderIsNotLive,
  getOrdersForDisplayedSecurities,
  isOrderChecked
} from './selectors'

const checkOrUncheckAllEpic: Epic<Action> = (action$, state$) =>
  action$.pipe(
    ofType<CheckOrUncheckAllAction>('checkedOrders.checkOrUncheckAllAction'),
    map((action) => {
      const { gridIndex, orderType } = action.payload
      const state = state$.value
      const status = checkedOrdersStatus(state)(gridIndex, orderType)
      switch (status) {
        case 'none':
        case 'some':
          // check all orders
          const userOrders = getOrdersForDisplayedSecurities(state)(
            gridIndex,
            orderType
          )
          const securityIdsFromCurrentWatchlist = getDetailsForCurrentWatchlist(
            state$.value
          )(gridIndex)?.securityIds
          const securitiesIdsToStageOrders =
            getStagedOrdersByTypeToArray(state)(orderType)
          const securityIds = [
            ...userOrders.map((order) => order.securityId),
            ...securitiesIdsToStageOrders.map(
              (securitiesIdsToStageOrder) =>
                securitiesIdsToStageOrder.securityId
            )
          ]
          const filteredSecurityIds = securityIds.filter((secId) =>
            securityIdsFromCurrentWatchlist?.includes(secId)
          )

          return updateCheckedOrders(orderType, [
            ...new Set(
              securityIdsFromCurrentWatchlist
                ? filteredSecurityIds
                : securityIds
            )
          ])
        case 'all':
          // uncheck all orders
          return updateCheckedOrders(orderType, [])
      }
    })
  )

const cancelCheckedOrdersEpic: Epic<Action> = (action$, state$) =>
  action$.pipe(
    ofType('checkedOrders.cancelCheckedOrders'),
    map((action: CancelCheckedOrdersAction) => {
      const state = state$.value
      const userOrders = getUserOrders(state)
      const isChecked = isOrderChecked(state)
      const orderIds = userOrders
        .filter(
          (order) =>
            order.status === 'pending' &&
            isChecked(order.securityId, order.type)
        )
        .map((order) => order.id)
      return cancelOrders(orderIds, action.payload.updateStagedOrder)
    })
  )

const orderCreatedEpic: Epic<Action> = (action$, state$) =>
  action$.pipe(
    ofType('order.createOrder'),
    map((action: CreateOrderAction) => {
      return uncheckOrder(action.payload.securityId, action.payload.order.type)
    })
  )

const fetchSecuritiesEpic: Epic<Action> = (action$, state$) =>
  action$.pipe(
    ofType('watchList.fetchWatchlistDetailsSuccess'),
    map((action: CreateOrderAction) => {
      return applyTempCheckedOrders()
    })
  )

const ordersCreatedEpic: Epic<Action> = (action$, state$) =>
  action$.pipe(
    ofType('order.createOrders'),
    map((action: CreateOrdersAction) => {
      const params = action.payload.params.map((p) => ({
        securityId: p.securityId,
        orderType: p.orderType
      }))
      return uncheckOrders(params)
    })
  )

const submitCheckedOrdersEpic: Epic<Action> = (action$, state$) =>
  action$.pipe(
    ofType('checkedOrders.submitCheckedOrders'),
    map(() => {
      const checkedStagedOrdersIsNotLive = getCheckedStagedOrderIsNotLive(
        state$.value
      )
      return createOrders(checkedStagedOrdersIsNotLive)
    })
  )

const cancelLiveOrderIfUseClearButton: Epic<Action> = (action$) =>
  action$.pipe(
    ofType('stagedOrders.removeStagedOrder'),
    map(() => cancelCheckedOrders(false))
  )

const cancelLiveOrdersIfUseClearButton: Epic<Action> = (action$) =>
  action$.pipe(
    ofType('stagedOrders.removeStagedOrders'),
    map(() => cancelCheckedOrders(false))
  )

const clearCheckOrderIfUseClearButton: Epic<Action> = (action$) =>
  action$.pipe(
    ofType('stagedOrders.removeStagedOrder'),
    map(() => resetCheckedOrders())
  )

const clearCheckOrdersIfUseClearButton: Epic<Action> = (action$) =>
  action$.pipe(
    ofType('stagedOrders.removeStagedOrders'),
    map(() => resetCheckedOrders())
  )

const clearCheckBoxAfterRemoveStagedOrder: Epic<Action> = (action$, state$) =>
  action$.pipe(
    ofType('stagedOrders.removeStagedOrder'),
    mergeMap((action: RemoveStagedOrderAction) => {
      const state = state$.value
      const checkOrdersArr = getCheckedOrders(state)
      return from(
        checkOrdersArr
          .filter(
            (checkOrder) =>
              checkOrder.securityId === action.payload.securityId &&
              checkOrder.orderType === action.payload.orderType &&
              getOrderBySecurityAndType(state)(
                action.payload.securityId,
                action.payload.orderType
              )?.status !== 'pending'
          )
          .map((order) => uncheckOrder(order.securityId, order.orderType))
      )
    })
  )

export default combineEpics(
  checkOrUncheckAllEpic,
  cancelCheckedOrdersEpic,
  submitCheckedOrdersEpic,
  clearCheckBoxAfterRemoveStagedOrder,
  cancelLiveOrderIfUseClearButton,
  cancelLiveOrdersIfUseClearButton,
  clearCheckOrderIfUseClearButton,
  clearCheckOrdersIfUseClearButton,
  orderCreatedEpic,
  ordersCreatedEpic,
  fetchSecuritiesEpic
)
